import translations from "@/config/translations";
import useFetch from "@/hooks/useFetch";
import useLangStore from "@/hooks/useLangStore";
import { MenuItem, TextField } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import "./styles.css";

// ── Types ─────────────────────────────────────────────────────────────────────

interface OrderItem {
    _id?: string;
    product: any;
    name: string;
    price: number;
    quantity: number;
}

interface Order {
    _id: string;
    number: string;
    customer?: {
        _id: string;
        name: string;
        phone: string;
        picture?: string;
    };
    branch?: {
        _id: string;
        name: string | { lang: string; value: string }[];
        address?: string | { lang: string; value: string }[];
    };
    totalPrice: number;
    status: "completed" | "pending" | "rejected" | "cancelled";
    date: string;
    pickupCode?: string;
    source: "customer" | "client" | "cashier";
    items: OrderItem[];
    createdAt: string;
}

export interface OrderDetailsPopupDetails {
    orderId: string;
    onSuccess?: () => void;
}

interface Props {
    onClose: () => void;
    details?: OrderDetailsPopupDetails;
}

// ── MUI Styles ────────────────────────────────────────────────────────────────

const getTextFieldSx = (dir: "rtl" | "ltr") => ({
    direction: dir,
    "& .MuiInputLabel-root": {
        fontFamily: "Alexandria, sans-serif",
        fontSize: "13.5px",
        color: "var(--color-gray-02)",
        transformOrigin: dir === "rtl" ? "top right" : "top left",
        right: dir === "rtl" ? 0 : "unset",
        left: dir === "rtl" ? "unset" : 0,
        "&:not(.MuiInputLabel-shrink)": {
            transform: dir === "rtl" ? "translate(-14px, 14px) scale(1)" : "translate(14px, 14px) scale(1)",
        },
        "&.MuiInputLabel-shrink": {
            transform: dir === "rtl" ? "translate(-14px, -9px) scale(0.75)" : "translate(14px, -9px) scale(0.75)",
        },
        "&.Mui-focused": {
            color: "var(--color-orange)",
        },
    },
    "& .MuiOutlinedInput-root": {
        fontFamily: "Alexandria, sans-serif",
        fontSize: "13.5px",
        color: "var(--color-dark)",
        backgroundColor: "var(--color-white)",
        borderRadius: "var(--radius-lev3)",
        "& fieldset": {
            borderColor: "var(--color-gray-01)",
            borderWidth: "1.5px",
        },
        "&:hover fieldset": {
            borderColor: "var(--color-gray-02)",
        },
        "&.Mui-focused fieldset": {
            borderColor: "var(--color-orange)",
            borderWidth: "1.5px",
        },
    },
    "& .MuiSelect-select": {
        fontFamily: "Alexandria, sans-serif",
        padding: "14px !important",
        paddingRight: dir === "rtl" ? "14px !important" : "32px !important",
        paddingLeft: dir === "rtl" ? "32px !important" : "14px !important",
        textAlign: dir === "rtl" ? "right" : "left",
    },
    "& fieldset legend": {
        textAlign: dir === "rtl" ? "right" : "left",
        marginLeft: dir === "rtl" ? "auto" : undefined,
        marginRight: dir === "rtl" ? "10px" : undefined,
    },
    "& .MuiSelect-icon": {
        right: dir === "rtl" ? "unset" : 7,
        left: dir === "rtl" ? 7 : "unset",
    },
});

// ── Component ─────────────────────────────────────────────────────────────────

export default function OrderDetailsPopup({ onClose, details }: Props) {
    const { lang } = useLangStore();
    const t = translations[lang ?? "ar"];
    const dir = lang === "ar" ? "rtl" : "ltr";
    const textFieldSx = getTextFieldSx(dir);

    const [order, setOrder] = useState<Order | null>(null);
    const [status, setStatus] = useState<Order["status"]>("pending");

    // ── Fetch order details ─────────────────────────────────────────────────────
    const { loading: fetchingOrder, data: orderData, invoke: fetchOrder } = useFetch("orders", { method: "GET" }, false);

    useEffect(() => {
        if (details?.orderId) {
            fetchOrder({ param: details.orderId });
        }
    }, [details?.orderId]);

    const wasFetching = useRef(false);
    useEffect(() => {
        if (fetchingOrder) {
            wasFetching.current = true;
        } else if (wasFetching.current) {
            wasFetching.current = false;
            const fetchedOrder = orderData?.data?.order;
            if (fetchedOrder) {
                setOrder(fetchedOrder);
                setStatus(fetchedOrder.status);
            }
        }
    }, [fetchingOrder, orderData]);

    // ── Update order status ─────────────────────────────────────────────────────
    const { loading: updating, status: updateStatus, invoke: updateOrder } = useFetch("orders", { method: "PUT" }, false);

    const wasUpdating = useRef(false);
    useEffect(() => {
        if (updating) {
            wasUpdating.current = true;
        } else if (wasUpdating.current) {
            wasUpdating.current = false;
            if (updateStatus === 200) {
                details?.onSuccess?.();
                onClose();
            }
        }
    }, [updating, updateStatus]);

    const handleSave = () => {
        if (!order || updating) return;
        updateOrder({ param: order._id, data: { status } });
    };

    // ── Helper Functions ────────────────────────────────────────────────────────
    const getLocalizedValue = (value: string | { lang: string; value: string }[] | undefined): string => {
        if (!value) return "—";
        if (typeof value === "string") return value;
        if (Array.isArray(value)) {
            const match = value.find((v) => v.lang === lang) ?? value[0];
            return match?.value ?? "—";
        }
        return "—";
    };

    const formatDate = (dateString: string): string => {
        if (!dateString) return "—";
        const date = new Date(dateString);
        return date.toLocaleDateString(lang === "ar" ? "ar-SA" : "en-US", {
            year: "numeric",
            month: "long",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    };

    const getStatusColor = (status: Order["status"]): string => {
        const colors = {
            completed: "odp-status-completed",
            pending: "odp-status-pending",
            rejected: "odp-status-rejected",
            cancelled: "odp-status-cancelled",
        };
        return colors[status] || "";
    };

    if (fetchingOrder || !order) {
        return (
            <div className="order-details-popup">
                <div className="odp-header">
                    <h2 className="odp-title">{t.orderDetails.title}</h2>
                    <button type="button" className="odp-close" onClick={onClose} aria-label="Close">
                        ×
                    </button>
                </div>
                <div className="odp-loading">{t.orderDetails.loading}</div>
            </div>
        );
    }

    return (
        <div className="order-details-popup">
            <div className="odp-header">
                <div className="odp-header-info">
                    <h2 className="odp-title">
                        {t.orderDetails.title} <span className="odp-number">#{order.number}</span>
                    </h2>
                    <span className={`odp-status ${getStatusColor(order.status)}`}>{t.orders.statuses[order.status]}</span>
                </div>
                <button type="button" className="odp-close" onClick={onClose} aria-label="Close">
                    ×
                </button>
            </div>

            <div className="odp-body">
                {/* Customer Info */}
                <div className="odp-section">
                    <h3 className="odp-section-title">{t.orderDetails.customerInfo}</h3>
                    <div className="odp-customer">
                        {order.customer?.picture ? (
                            <img src={order.customer.picture} alt={order.customer.name} className="odp-customer-picture" />
                        ) : (
                            <div className="odp-customer-placeholder">{order.customer?.name?.charAt(0) ?? "?"}</div>
                        )}
                        <div className="odp-customer-details">
                            <p className="odp-customer-name">{order.customer?.name || "—"}</p>
                            <p className="odp-customer-phone">{order.customer?.phone || "—"}</p>
                        </div>
                    </div>
                </div>

                {/* Branch Info */}
                {order.branch && (
                    <div className="odp-section">
                        <h3 className="odp-section-title">{t.orderDetails.branchInfo}</h3>
                        <p className="odp-branch-name">{getLocalizedValue(order.branch.name)}</p>
                        {order.branch.address && <p className="odp-branch-address">{getLocalizedValue(order.branch.address)}</p>}
                    </div>
                )}

                {/* Order Items */}
                <div className="odp-section">
                    <h3 className="odp-section-title">{t.orderDetails.items}</h3>
                    <div className="odp-items">
                        {order.items.map((item, index) => (
                            <div key={item._id || index} className="odp-item">
                                <div className="odp-item-info">
                                    <span className="odp-item-quantity">×{item.quantity}</span>
                                    <span className="odp-item-name">{item.name}</span>
                                </div>
                                <span className="odp-item-price">
                                    {(item.price * item.quantity).toFixed(2)} {t.orders.sar}
                                </span>
                            </div>
                        ))}
                    </div>
                    <div className="odp-total">
                        <span className="odp-total-label">{t.orderDetails.total}</span>
                        <span className="odp-total-price">
                            {order.totalPrice.toFixed(2)} {t.orders.sar}
                        </span>
                    </div>
                </div>

                {/* Order Details */}
                <div className="odp-section">
                    <h3 className="odp-section-title">{t.orderDetails.orderInfo}</h3>
                    <div className="odp-info-grid">
                        <div className="odp-info-item">
                            <span className="odp-info-label">{t.orderDetails.orderNumber}</span>
                            <span className="odp-info-value">#{order.number}</span>
                        </div>
                        <div className="odp-info-item">
                            <span className="odp-info-label">{t.orderDetails.source}</span>
                            <span className="odp-info-value">{t.orderDetails.sources[order.source]}</span>
                        </div>
                        <div className="odp-info-item">
                            <span className="odp-info-label">{t.orderDetails.date}</span>
                            <span className="odp-info-value">{formatDate(order.createdAt)}</span>
                        </div>
                        {order.pickupCode && (
                            <div className="odp-info-item">
                                <span className="odp-info-label">{t.orderDetails.pickupCode}</span>
                                <span className="odp-info-value odp-pickup-code">{order.pickupCode}</span>
                            </div>
                        )}
                    </div>
                </div>

                {/* Status Update */}
                <div className="odp-section">
                    <h3 className="odp-section-title">{t.orderDetails.updateStatus}</h3>
                    <TextField
                        select
                        fullWidth
                        label={t.orderDetails.statusLabel}
                        value={status}
                        onChange={(e) => setStatus(e.target.value as Order["status"])}
                        sx={textFieldSx}
                    >
                        <MenuItem value="pending">{t.orders.statuses.pending}</MenuItem>
                        <MenuItem value="completed">{t.orders.statuses.completed}</MenuItem>
                        <MenuItem value="rejected">{t.orders.statuses.rejected}</MenuItem>
                        <MenuItem value="cancelled">{t.orders.statuses.cancelled}</MenuItem>
                    </TextField>
                </div>
            </div>

            <div className="odp-footer">
                <button type="button" className="odp-btn odp-btn--cancel" onClick={onClose} disabled={updating}>
                    {t.orderDetails.cancel}
                </button>
                <button type="button" className="odp-btn odp-btn--save" onClick={handleSave} disabled={updating || status === order.status}>
                    {updating ? t.orderDetails.saving : t.orderDetails.save}
                </button>
            </div>
        </div>
    );
}
